import type { ScenarioInput } from '../types';

export const demoScenario: ScenarioInput = {
  scenario_id: null,
  name: "The $17,000 Surprise",
  description:
    "Two sedans, $8,000 apart at the dealership. See what five years of ownership actually costs.",
  horizon_months: 60,
  annual_miles: 15000,
  financial_weight: 0.7,
  emotional_weight: 0.3,
  dominance_epsilon_pct: 0.25,
  investment_return_rate: 0.07,
  sales_tax_rate: 0.06,
  winter_miles_pct: 0.4,
  state_code: 'MI',
  use_emotional_override: false,
  vehicles: [
    {
      vehicle_id: "00000000-0000-0000-0001-000000000001",
      label: "2026 Honda Accord Touring Hybrid",
      model_year: 2026,
      purchase_condition: "new",
      vehicle_type: "mainstream_sedan",
      depreciation: {
        purchase_price: 39495,
        down_payment: 0,
        residual_value_year_5: 21720,
        annual_mileage: 15000,
        depreciation_rate_override: null,
      },
      financing: {
        loan_term_months: 60,
        annual_interest_rate: 0.065,
        loan_amount: 0,
        monthly_payment: null,
      },
      operating_costs: {
        fuel_type: "gasoline",
        fuel_cost_per_gallon: 3.20,
        mpg: 49,
        wh_per_mile: null,
        electricity_cost_per_kwh: null,
        annual_maintenance_cost: 900,
        annual_insurance_premium: 1650,
        annual_registration_fee: 237,
        ev_registration_surcharge: null,
        out_of_warranty_repair_annual: 400,
        warranty_remaining_months: 36,
        is_staggered_tires: false,
        tire_replacement_interval_miles: 50000,
        tire_set_cost: 700,
        tire_interval_months: 54,
        front_tire_interval_months: null,
        front_tire_set_cost: null,
        rear_tire_interval_months: null,
        rear_tire_set_cost: null,
        mount_balance_per_event: 75,
        has_winter_tires: false,
        winter_tire_set_cost: 0,
        winter_tire_interval_months: 36,
        tire_age_months: 0,
        annual_parking_cost: 0,
        annual_tolls_cost: 0,
        sales_tax_rate: 0.06,
        winter_miles_pct: 0.4,
      },
      emotional: {
        scores: {
          driving_enjoyment: 3,
          powertrain_character: 3,
          practical_utility: 5,
          winter_confidence: 3,
          brand_trust: 4,
          status_prestige: 2,
          ownership_pride: 3,
          technology_features: 3,
        },
        weights: {
          driving_enjoyment: 1,
          powertrain_character: 1,
          practical_utility: 1,
          winter_confidence: 1,
          brand_trust: 1,
          status_prestige: 1,
          ownership_pride: 1,
          technology_features: 1,
        },
      },
      capital_allocation: {
        opportunity_cost_rate: 0.07,
        analysis_horizon_years: 5,
        include_opportunity_cost: true,
      },
      upfit_cost: 0,
      upfit_description: '',
      vocation_profile: null,
      loaded_hourly_labor_rate: 0,
      weekly_hours: 0,
      weeks_per_year: 50,
      operating_days_per_week: 5,
      drivers_per_vehicle: 1,
      ownership_period_years: 5.0,
      annual_miles_per_vehicle: null,
      daily_idle_hours_override: null,
      mpg_data_source: null,
      electricity_rate_per_kwh: 0.16,
      diesel_price_per_gallon: 4.00,
      charging_infrastructure_cost: 0,
      purchase_incentives: 0,
      expected_residual_value: null,
      residual_data_source: null,
      depreciation_curve_id: null,
    },
    {
      vehicle_id: "00000000-0000-0000-0002-000000000001",
      label: "2026 BMW 3 Series 330i",
      model_year: 2026,
      purchase_condition: "new",
      vehicle_type: "luxury_sedan",
      depreciation: {
        purchase_price: 48675,
        down_payment: 0,
        residual_value_year_5: 23850,
        annual_mileage: 15000,
        depreciation_rate_override: null,
      },
      financing: {
        loan_term_months: 60,
        annual_interest_rate: 0.065,
        loan_amount: 0,
        monthly_payment: null,
      },
      operating_costs: {
        fuel_type: "gasoline",
        fuel_cost_per_gallon: 3.85,
        mpg: 31,
        wh_per_mile: null,
        electricity_cost_per_kwh: null,
        annual_maintenance_cost: 1400,
        annual_insurance_premium: 2100,
        annual_registration_fee: 388,
        ev_registration_surcharge: null,
        out_of_warranty_repair_annual: 1100,
        warranty_remaining_months: 48,
        is_staggered_tires: false,
        tire_replacement_interval_miles: 40000,
        tire_set_cost: 1300,
        tire_interval_months: 42,
        front_tire_interval_months: null,
        front_tire_set_cost: null,
        rear_tire_interval_months: null,
        rear_tire_set_cost: null,
        mount_balance_per_event: 100,
        has_winter_tires: false,
        winter_tire_set_cost: 0,
        winter_tire_interval_months: 36,
        tire_age_months: 0,
        annual_parking_cost: 0,
        annual_tolls_cost: 0,
        sales_tax_rate: 0.06,
        winter_miles_pct: 0.4,
        requires_premium: true,
      },
      emotional: {
        scores: {
          driving_enjoyment: 5,
          powertrain_character: 4,
          practical_utility: 3,
          winter_confidence: 3,
          brand_trust: 3,
          status_prestige: 5,
          ownership_pride: 4,
          technology_features: 4,
        },
        weights: {
          driving_enjoyment: 1,
          powertrain_character: 1,
          practical_utility: 1,
          winter_confidence: 1,
          brand_trust: 1,
          status_prestige: 1,
          ownership_pride: 1,
          technology_features: 1,
        },
      },
      capital_allocation: {
        opportunity_cost_rate: 0.07,
        analysis_horizon_years: 5,
        include_opportunity_cost: true,
      },
      upfit_cost: 0,
      upfit_description: '',
      vocation_profile: null,
      loaded_hourly_labor_rate: 0,
      weekly_hours: 0,
      weeks_per_year: 50,
      operating_days_per_week: 5,
      drivers_per_vehicle: 1,
      ownership_period_years: 5.0,
      annual_miles_per_vehicle: null,
      daily_idle_hours_override: null,
      mpg_data_source: null,
      electricity_rate_per_kwh: 0.16,
      diesel_price_per_gallon: 4.00,
      charging_infrastructure_cost: 0,
      purchase_incentives: 0,
      expected_residual_value: null,
      residual_data_source: null,
      depreciation_curve_id: null,
    },
  ],
};
